/*
|--------------------------------------------------------------------------
| Homepage Settings Page
| Description: إعدادات الصفحة الرئيسية - About Us, Why Us مع دعم اللغات
|--------------------------------------------------------------------------
*/

import { useEffect, useState, useCallback, useRef } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Spinner } from "react-bootstrap";
import PageTitle from "../../layouts/PageTitle";
import FormField from "../../components/ui/FormField";
import TextAreaField from "../../components/ui/TextAreaField";
import SubmitButton from "../../components/ui/SubmitButton";
import MediaSelectorButton from "../../components/media/MediaSelectorButton";
import LanguageSwitcherTabs from "../../components/ui/LanguageSwitcherTabs";
import {
  fetchSiteSettings,
  updateSiteSettings,
} from "../../../store/custom-actions/settings-actions/siteSettingsSlicer";

const SECTIONS = [
  { id: "about_us", label: "About Us", icon: "bi bi-info-circle" },
  { id: "why_us", label: "Why Us", icon: "bi bi-question-circle" },
];

const TRANSLATABLE_FIELDS = ["title", "content"];

/** خيارات هذه الصفحة فقط - لاستدعاء API بأداء أفضل (option_name) */
const HOMEPAGE_OPTION_NAMES =
  "about_us_title,about_us_content,about_us_image,why_us_title,why_us_content,why_us_video";
/** المفاتيح التي يُرجعها الباك اند كـ Upload (upload_keys) */
const HOMEPAGE_UPLOAD_KEYS = "about_us_image,why_us_video";

function buildInitialLangValues(languages) {
  const byLang = {};
  (languages || []).forEach((lang) => {
    byLang[lang.slug] = "";
  });
  return {
    about_us: { title: { ...byLang }, content: { ...byLang } },
    why_us: { title: { ...byLang }, content: { ...byLang } },
  };
}

function getOptionValue(data, key) {
  const v = data?.[key];
  if (v == null) return undefined;
  if (typeof v === "object" && v?.id != null) return v.id;
  return typeof v === "string" || typeof v === "number" ? v : undefined;
}

function getOptionForMedia(data, key) {
  const v = data?.[key];
  if (!v || typeof v !== "object" || !v.id) return [];
  return [{ id: v.id, path: v.path, type: v.type }];
}

function SectionFormFields({
  t,
  register,
  watch,
  setValue,
  errors,
  mediaType,
  mediaLabelKey,
  inputName,
  defaultSelected,
}) {
  return (
    <div className="row">
      <div className="col-12 mb-3">
        <FormField
          label={t("Title")}
          name="title"
          register={register}
          errors={errors}
        />
      </div>
      <div className="col-12 mb-3">
        <TextAreaField
          label={t("Content")}
          name="content"
          register={register}
          watch={watch}
          setValue={setValue}
          errors={errors}
          isRichTextEditor
        />
      </div>
      <div className="col-12 col-md-6 mb-3">
        <MediaSelectorButton
          label={t(mediaLabelKey)}
          inputName={inputName}
          mediaType={mediaType}
          register={register}
          errors={errors}
          onChange={(value) => setValue(inputName, value)}
          defaultSelected={defaultSelected}
        />
      </div>
    </div>
  );
}

function HomepageSettingsPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { loading: submitLoading } = useSelector(
    (state) => state.siteSettingsSlicer
  );
  const { data: languageData } = useSelector(
    (state) => state.getLanguageSettingsSlicer
  );

  const [activeSection, setActiveSection] = useState(SECTIONS[0].id);
  const [activeLang, setActiveLang] = useState(null);
  const [langValues, setLangValues] = useState(null);
  const [homePageMediaData, setHomePageMediaData] = useState({
    about_us_image: [],
    why_us_video: [],
  });
  const [homepageLoading, setHomepageLoading] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm();

  const languages = languageData?.languages || [];
  const defaultLangSlug = languageData?.defaultLang?.slug;

  const languagesRef = useRef(languages);
  const lastFetchedLangRef = useRef(null);
  const langValuesRef = useRef(langValues);
  const activeSectionRef = useRef(activeSection);
  languagesRef.current = languages;
  langValuesRef.current = langValues;
  activeSectionRef.current = activeSection;

  // Initialize langValues and activeLang when languages load
  useEffect(() => {
    if (!languages.length) return;
    if (langValues === null) {
      setLangValues(buildInitialLangValues(languages));
    }
    if (activeLang === null && defaultLangSlug) {
      setActiveLang(defaultLangSlug);
    }
  }, [languages, defaultLangSlug, activeLang, langValues]);

  // جلب إعدادات الصفحة الرئيسية للغة النشطة فقط (مرجع ثابت لتفادي إعادة الطلب مع كل render)
  const fetchHomepageData = useCallback(async (lang) => {
    if (!lang) return;
    lastFetchedLangRef.current = lang;
    setHomepageLoading(true);
    try {
      const res = await dispatch(
        fetchSiteSettings({
          option_name: HOMEPAGE_OPTION_NAMES,
          lang,
          upload_keys: HOMEPAGE_UPLOAD_KEYS,
        })
      );
      if (lastFetchedLangRef.current !== lang) return;
      const data = res.meta?.requestStatus === "fulfilled" ? res.payload : null;
      if (!data) return;
      const langs = languagesRef.current;
      setLangValues((prev) => {
        const next = prev
          ? JSON.parse(JSON.stringify(prev))
          : buildInitialLangValues(langs);
        next.about_us.title[lang] = data.about_us_title ?? "";
        next.about_us.content[lang] = data.about_us_content ?? "";
        next.why_us.title[lang] = data.why_us_title ?? "";
        next.why_us.content[lang] = data.why_us_content ?? "";
        return next;
      });
      setValue("about_us_image", getOptionValue(data, "about_us_image"));
      setValue("why_us_video", getOptionValue(data, "why_us_video"));
      setHomePageMediaData({
        about_us_image: getOptionForMedia(data, "about_us_image"),
        why_us_video: getOptionForMedia(data, "why_us_video"),
      });
      const section = activeSectionRef.current;
      setValue("title", section === "about_us" ? (data.about_us_title ?? "") : (data.why_us_title ?? ""));
      setValue("content", section === "about_us" ? (data.about_us_content ?? "") : (data.why_us_content ?? ""));
    } finally {
      if (lastFetchedLangRef.current === lang) setHomepageLoading(false);
    }
  }, [dispatch, setValue]);

  const fetchHomepageDataRef = useRef(fetchHomepageData);
  fetchHomepageDataRef.current = fetchHomepageData;
  const prevActiveLangRef = useRef(null);
  useEffect(() => {
    if (!languages.length || !activeLang) return;
    if (prevActiveLangRef.current === activeLang) return;
    prevActiveLangRef.current = activeLang;
    fetchHomepageDataRef.current(activeLang);
  }, [activeLang, languages.length]);

  // عند تغيير القسم أو اللغة فقط: مزامنة الحقول من langValues (قراءة من ref لتفادي حلقة مع watch)
  useEffect(() => {
    if (activeLang == null) return;
    const values = langValuesRef.current;
    if (!values) return;
    const section = activeSection;
    setValue("title", values[section]?.title?.[activeLang] ?? "");
    setValue("content", values[section]?.content?.[activeLang] ?? "");
  }, [activeSection, activeLang, setValue]);

  // Sync form title/content back to langValues when user types — فقط عند تغيير القيمة فعلياً لتفادي حلقة
  const watchedTitle = watch("title");
  const watchedContent = watch("content");
  useEffect(() => {
    const values = langValuesRef.current;
    if (activeLang == null || !values) return;
    const section = activeSection;
    const prevTitle = values[section]?.title?.[activeLang];
    const prevContent = values[section]?.content?.[activeLang];
    const newTitle = watchedTitle ?? "";
    const newContent = watchedContent ?? "";
    if (prevTitle === newTitle && prevContent === newContent) return;
    setLangValues((prev) => ({
        about_us:
          section === "about_us"
            ? {
                title: { ...prev.about_us?.title, [activeLang]: newTitle },
                content: { ...prev.about_us?.content, [activeLang]: newContent },
              }
            : prev.about_us,
        why_us:
          section === "why_us"
            ? {
                title: { ...prev.why_us?.title, [activeLang]: newTitle },
                content: { ...prev.why_us?.content, [activeLang]: newContent },
              }
            : prev.why_us,
    }));
  }, [watchedTitle, watchedContent, activeSection, activeLang]);

  const onSubmit = useCallback(
    async (formData) => {
      const currentLang = activeLang || defaultLangSlug || languages[0]?.slug;
      const payload = {
        about_us_title: langValues?.about_us?.title?.[currentLang] ?? "",
        about_us_content: langValues?.about_us?.content?.[currentLang] ?? "",
        about_us_image: formData.about_us_image ?? "",
        why_us_title: langValues?.why_us?.title?.[currentLang] ?? "",
        why_us_content: langValues?.why_us?.content?.[currentLang] ?? "",
        why_us_video: formData.why_us_video ?? "",
        lang: {
          about_us_title: currentLang,
          about_us_content: currentLang,
          why_us_title: currentLang,
          why_us_content: currentLang,
        },
      };
      await dispatch(updateSiteSettings(payload));
      fetchHomepageData(activeLang || defaultLangSlug || languages[0]?.slug);
    },
    [langValues, activeLang, defaultLangSlug, languages, dispatch, fetchHomepageData]
  );

  const aboutUsImageDefault = homePageMediaData.about_us_image;
  const whyUsVideoDefault = homePageMediaData.why_us_video;

  return (
    <>
      <PageTitle
        motherMenu={t("Settings")}
        activeMenu={t("Homepage Settings")}
        customClasses="mb-0"
      />
      <div className="row">
        {/* Nav: اختيار نوع الإعدادات */}
        <div className="col-xl-3 col-lg-4 col-md-4 mb-4">
          <div className="card">
            <div className="card-body p-0">
              <nav className="nav flex-column">
                {SECTIONS.map((sec) => (
                  <button
                    key={sec.id}
                    type="button"
                    onClick={() => setActiveSection(sec.id)}
                    className={`nav-link text-start d-flex align-items-center gap-2 py-3 px-4 border-0 rounded-0 ${
                      activeSection === sec.id ? "bg-primary text-white" : ""
                    }`}
                    aria-current={activeSection === sec.id ? "true" : undefined}
                  >
                    <i className={sec.icon} />
                    {t(sec.label)}
                  </button>
                ))}
              </nav>
            </div>
          </div>
        </div>

        {/* Card: نموذج الإعدادات */}
        <div className="col-xl-9 col-lg-8 col-md-8">
          <form id="homepage-settings-form" onSubmit={handleSubmit(onSubmit)}>
            <div className="p-2 mb-3 mt-0">
              <SubmitButton
                isLoading={submitLoading}
                form="homepage-settings-form"
              />
            </div>

            <div className="card">
              <h5 className="card-title p-4">
                {t(activeSection === "about_us" ? "About Us" : "Why Us")}
              </h5>
              <div className="card-body">
                <LanguageSwitcherTabs
                  languages={languages}
                  activeLang={activeLang}
                  onChange={setActiveLang}
                />
                {activeLang && (
                  <p className="text-muted small mb-3">
                    {t("Values will be saved for the current language only")}{" "}
                    ({languages.find((l) => l.slug === activeLang)?.name ?? activeLang})
                  </p>
                )}

                {homepageLoading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted mb-0">{t("Loading...")}</p>
                  </div>
                ) : (
                  <>
                    {activeSection === "about_us" && (
                      <SectionFormFields
                        t={t}
                        register={register}
                        watch={watch}
                        setValue={setValue}
                        errors={errors}
                        mediaType="image"
                        mediaLabelKey="Image"
                        inputName="about_us_image"
                        defaultSelected={aboutUsImageDefault}
                      />
                    )}
                    {activeSection === "why_us" && (
                      <SectionFormFields
                        t={t}
                        register={register}
                        watch={watch}
                        setValue={setValue}
                        errors={errors}
                        mediaType="video"
                        mediaLabelKey="Video"
                        inputName="why_us_video"
                        defaultSelected={whyUsVideoDefault}
                      />
                    )}
                  </>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

export default HomepageSettingsPage;
